import {
  Color,
  Material,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  Object3D,
  Scene,
  SRGBColorSpace,
  Texture,
  Vector2,
  Vector3,
  Vector4,
} from "three";
import {
  convertMaterialsToPhysical,
  listMaterialByName,
  listMeshByName,
} from "../util/SceneUtil";
import { LoaderManager } from "./Loader";
import { SugarAsset, SugarModel } from "./SugarModel";
import { SugarTextureLoader } from "./SugarTextureLoader";

const UP = new Vector3(0, 1, 0);
const FORWARD = new Vector3(0, 0, 1);
const RIGHT = new Vector3(1, 0, 1);

const modelCache: Map<string, SugarModel> = new Map<string, SugarModel>();

const partCache: Map<string, SugarPart> = new Map<string, SugarPart>();

const materialCache: Map<string, SugarMaterial> = new Map<
  string,
  SugarMaterial
>();

Object3D.prototype.getScreenPosition = function (out: Vector4) {
  return new Vector4();
};

Object3D.prototype.detach = function (): void {
  let pp = this.parent;
  while (pp && !(pp instanceof Scene)) pp = pp.parent;
  if (pp) {
    pp.attach(this);
  } else {
    this.updateMatrixWorld(true);
    const worldMatrix = this.matrixWorld.clone();

    if (this.parent) {
      this.parent.remove(this);
    }
    this.matrix.copy(worldMatrix);
    this.matrix.decompose(this.position, this.quaternion, this.scale);
    this.updateMatrixWorld(true);
  }
};

export const createPart = (partData: any) => {
  let part = undefined; //; partCache.get(partData.id);
  if (!part) {
    part = new SugarPart(partData);
    partCache.set(partData.id, part);
  }
  return part;
};

export const createMaterial = (materialData: any, key?: string) => {
  const keyId = key ? key : materialData.id;
  let material = materialCache.get(keyId);
  if (!material) {
    material = new SugarMaterial(materialData);
    materialCache.set(keyId, material);
  }
  return material;
};

export const clearCache = () => {
  partCache.clear();
  materialCache.clear();
};

export class SugarModelBase extends Object3D {
  public root = new Object3D();

  constructor() {
    super();
    this.root.name = "root";
    this.add(this.root);
  }

  private _freeze = false;
  get freeze() {
    return this._freeze;
  }

  set freeze(value) {
    this._freeze = value;
  }

  private _removeable = false;
  get removeable() {
    return this._removeable;
  }

  set removeable(value) {
    this.removeable = value;
  }

  set configurator(value: SugarModel | undefined) {
    this.userData["configurator"] = value;
  }

  get configurator() {
    return this.userData["configurator"];
  }

  hasIt(object: Object3D, parent?: Object3D) {
    const check = parent ? parent : this;
    if (check == object.parent) return true;
    for (let i = 0; check.children.length > i; i++)
      if (this.hasIt(object, check.children[i])) return true;

    return false;
  }

  dispose() {}
}

/**
 *
 *
 *
 *
 *
 */
export class SugarPart implements SugarAsset {
  private _data: any;
  private _isLoaded = false;

  materials: SugarMaterial[] = [];
  baseAoMap: Texture | undefined = undefined;
  baseNormalMap: Texture | undefined = undefined;

  constructor(part: any) {
    this._data = part;
    this.materials = this._data.materials?.map((id: any) => {
      return materialCache.get(id);
    });
  }

  get data(): any {
    return this._data;
  }

  get id(): string {
    return this._data.id;
  }

  get name(): string {
    return this._data.name;
  }

  get code(): string {
    return this._data.code;
  }

  get thumbnailFileUrl(): string {
    return this._data.thumbnailFileUrl;
  }

  get size() {
    return this._data.size ? this._data.size : 1;
  }

  get loaded() {
    return this._isLoaded;
  }

  async load() {
    if (this._isLoaded) return;

    const loader = new LoaderManager(new SugarTextureLoader());

    const partsLooaded = await Promise.all([
      loader.load(this._data.baseNormalMap),
      loader.load(this._data.baseAoMap),
    ]);

    this.baseNormalMap = partsLooaded[0];
    if (this.baseNormalMap) this.baseNormalMap.needsUpdate = true;

    this.baseAoMap = partsLooaded[1];
    if (this.baseAoMap) {
      this.baseAoMap.repeat.x = 1 / this.size;
      this.baseAoMap.repeat.y = 1 / this.size;
      this.baseAoMap.needsUpdate = true;
    }
    this._isLoaded = true;
  }

  async apply(object: Object3D) {
    convertMaterialsToPhysical(object);

    const meshes = listMeshByName(object, this._data.name);
    meshes.forEach((mesh) => {
      const geometry = mesh.geometry;

      if (geometry.userData.sized) return;

      const uv = geometry.attributes.uv;

      for (let i = 0; i < uv.count; i++) {
        const u = uv.getX(i);
        const v = uv.getY(i);
        uv.setXY(i, u * this.size, v * this.size);
      }

      uv.needsUpdate = true;
      geometry.userData.sized = true;
      mesh.geometry = geometry;
    });

    const materials = listMaterialByName(object, this._data.name);
    materials.forEach((material) => {
      const mMap = material as MeshStandardMaterial | MeshPhysicalMaterial;
      /*| MeshTransmissionMaterial*/ if (this.baseAoMap) {
        mMap.aoMap = this.baseAoMap;
        this.baseAoMap.flipY = false;
        mMap.aoMapIntensity = 0.95;
      }
      mMap.envMapIntensity = 1.05;

      if (this.baseNormalMap) {
        mMap.userData.baseNormalMap = this.baseNormalMap;
        this.baseNormalMap.flipY = false;
      }

      mMap.needsUpdate = true;
      //console.log("material id ", mMap.uuid, mMap.id, object.name, object.uuid);
    });
  }
}

export interface SugarModelJointInterface {
  get direction(): Vector3 | string;
  //get up(): Vector3 | string;
  get id(): number;
  get inType(): string;
  get modelId(): string;
  get offset(): Vector3 | string;
  get outType(): string;
  get position(): Vector3 | string;
}

/**
 *
 *
 *
 *
 *
 */
export class SugarMaterial implements SugarAsset {
  private _data: any;
  private _isLoaded = false;
  private _material?: MeshPhysicalMaterial;

  constructor(data: any) {
    this._data = data;
  }

  get data(): any {
    return this._data;
  }
  get id(): string {
    return this._data.id;
  }

  get name(): string {
    return this._data.name;
  }

  get code(): string {
    return this._data.groupBy_;
  }

  get thumbnailFileUrl(): string {
    return this._data.thumbnailFile;
  }

  get loaded() {
    return this._isLoaded;
  }

  private _map: Texture | undefined;
  private _normalMap: Texture | undefined;
  private _aoMap: Texture | undefined;
  private _roughnessMap: Texture | undefined;
  private _emissiveMap: Texture | undefined;

  async load() {
    if (this._isLoaded) return this;

    const loader = new LoaderManager(new SugarTextureLoader());
    let diffuse = this._data.diffuse || "";

    const findMap = (map: any) => {
      return !map
        ? ""
        : map.uri512
          ? map.uri512
          : map.uri2
            ? map.uri2
            : map.uri4
              ? map.uri4
              : map.uri1
                ? map.uri1
                : "";
    };

    if (diffuse.uri1 || diffuse.uri2 || diffuse.uri4 || diffuse.uri512) {
      diffuse = findMap(this._data.diffuse);
      diffuse += ";" + findMap(this._data.roughness);
      diffuse += ";" + findMap(this._data.normal);
      diffuse += ";" + findMap(this._data.ao);
      diffuse += ";" + findMap(this._data.emissiveMap);
    }

    const mapUris = diffuse.split(";");

    const mapLoaded = await Promise.all([
      loader.load(mapUris[0]),
      loader.load(mapUris[1]),
      loader.load(mapUris[2]),
      loader.load(mapUris[3]),
      loader.load(mapUris[4]),
    ]);

    mapLoaded.forEach((m) => {
      if (m) {
        m.flipY = false;
        m.needsUpdate = true;
      }
    });
    this._map = mapLoaded[0];
    this._roughnessMap = mapLoaded[1];
    this._normalMap = mapLoaded[2];
    this._aoMap = mapLoaded[3];
    this._emissiveMap = mapLoaded[4];

    this._isLoaded = true;
    return this;
  }

  async apply(material: Material) {
    const mMap = material as MeshStandardMaterial | MeshPhysicalMaterial;

    const color = this._data.color;
    mMap.color = color != null ? new Color("#" + color) : new Color(0xffffff);

    const transparent = this._data.transparent;
    mMap.transparent = transparent && transparent > 0;

    const opacity = this._data.opacity;
    mMap.opacity = opacity != null ? opacity : mMap.opacity;

    const depthTest = mMap.depthTest;
    mMap.depthTest = depthTest != null ? depthTest : mMap.depthTest;

    const depthWrite = mMap.depthWrite;
    mMap.depthWrite = depthWrite != null ? depthWrite : mMap.depthWrite;

    let size = this._data.size ? this._data.size : 1;
    //size *= options.size ? options.size : 1;

    if (this._map) {
      mMap.map = this._map;
      mMap.map.repeat.x = size;
      mMap.map.repeat.y = size;
      mMap.map.colorSpace = SRGBColorSpace;
    }

    mMap.normalMap = null;
    if (this._normalMap) {
      mMap.normalMap = this._normalMap;
      mMap.normalMap.repeat.x = size;
      mMap.normalMap.repeat.y = size;
      mMap.normalScale = new Vector2(2.5, 2.5);
    }

    mMap.roughnessMap = null;
    mMap.metalnessMap = null;
    mMap.metalness = this._data.metallicFactor;
    mMap.roughness = this._data.roughnessFactor;
    if (this._roughnessMap) {
      mMap.roughnessMap = this._roughnessMap;
      mMap.metalnessMap = this._roughnessMap;
      mMap.roughnessMap.repeat.x = size;
      mMap.roughnessMap.repeat.y = size;
      mMap.metalnessMap.repeat.x = size;
      mMap.metalnessMap.repeat.y = size;
    }

    if (this._aoMap) {
      mMap.aoMap = this._aoMap;
    }

    mMap.emissiveMap = null;
    if (this._emissiveMap) {
      mMap.emissiveMap = this._emissiveMap;
      mMap.emissiveMap.repeat.x = size;
      mMap.emissiveMap.repeat.y = size;
    }

    mMap.needsUpdate = true;
  }
}

/**
 *
 *
 *
 *
 */
export class SugarPartGroup implements SugarAsset {
  private _data: any;

  private _parts: SugarPart[] = [];
  private _materials: SugarMaterial[] = [];
  private _material?: SugarMaterial;
  private _model?: Object3D;
  private _isLoaded = false;

  get data(): any {
    return this._data;
  }
  get id(): string {
    return this._data.id;
  }
  get name(): string {
    return this._data.title;
  }
  get code(): string {
    return this._data.code;
  }
  get material(): SugarMaterial | undefined {
    return this._material;
  }
  set material(value: SugarMaterial) {
    this._material = value;
    this.apply(this._model!);
  }

  get thumbnailFileUrl(): string {
    return this._data.thumbnailFileUrl;
  }

  get materials() {
    return this._materials;
  }

  get loaded() {
    return this._isLoaded;
  }

  constructor(
    partMaterialGroup: any,
    materials: SugarMaterial[],
    parts: SugarPart[],
  ) {
    this._data = partMaterialGroup;

    const code = this._data.code;
    const defaultMaterialCode = this._data.defaultMaterialCode;

    //find default material
    let filteredMaterials = materials?.filter((material) => {
      return (
        material.code?.toUpperCase().trim() === code.toUpperCase().trim() ||
        material.id == defaultMaterialCode
      );
    });
    if (filteredMaterials && filteredMaterials.length > 0)
      this._material = filteredMaterials[0];

    //find by code parts
    this._parts = parts.filter((part) => {
      return part.code === code;
    });

    //find possible materials
    this._materials = [];
    this._parts.forEach((part) => {
      this._materials = [...part.materials, ...this._materials];
    });

    this._materials = this._materials
      .filter((m) => m)
      .reduce((acc: SugarMaterial[], current) => {
        const x = acc.findIndex((item) => item.id === current.id);
        if (x <= -1) {
          acc.push(current);
        }
        return acc;
      }, []);

    /*.map((part) => {
        return createPart(part);
      });*/

    //merge with group parts part
    /*if (this._partMaterialGroup.part)
      this._parts = [...this._parts, ...this._partMaterialGroup.part];*/
  }

  async load() {
    await this._material?.load();
    this._isLoaded = true;
  }

  async apply(object: Object3D, material?: SugarMaterial) {
    this._model = object;

    let m = material ? await material?.load() : await this._material?.load();
    this._material = m;
    this._parts.forEach((part) => {
      let materialInstances = listMaterialByName(object, part.name);
      materialInstances.forEach((materialInstance) => {
        //can a part have multiple materials
        m?.apply(materialInstance);
      });
    });
  }
}

const getColorOfType = (type: string): Color | undefined => {
  if (type === "sag") {
    return new Color(1.0, 0, 0);
  }
  if (type === "sol") {
    return new Color(0.0, 1.0, 0);
  }
  if (type === "arka") {
    return new Color(0.0, 0, 1.0);
  }
  if (type === "on") {
    return new Color(1.0, 0, 1.0);
  }
};
